import React, { useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FormControl, MenuItem, Paper, SelectChangeEvent } from '@mui/material';
import { range, isNil, reverse } from 'ramda';
import { useDispatch } from 'react-redux';
import {
  setGrowthYears,
  setSelectedYear,
  updateScenario,
} from 'spw-redux/modules/calculators/actions';
import {
  CalculatorType,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  YEARS_AHEAD,
} from 'spw-redux/modules/calculators/types';
import {
  IBuilding,
  IDepartment,
  IFloor,
  IProject,
} from 'spw-redux/modules/projects/types';
import { getDepartmentsByProjectId } from 'spw-redux/modules/projects/actions';
import { getWidthForString, isDepartmentsTypeScenario } from 'spw-shared/utils';

import { ScenarioHeader } from 'spw-components/downloads/image-header';
import { GROWTH_CALCULATORS } from 'spw-constants';
import DeskSharingGuide from './desk-sharing-guide';
import HeadcountUtilizationGuide from './headcount-utilization-guide';
import VacancyFactorGuide from './vacancy-factor-guide';
import Headcount from './headcount';
import GrowthTable from './growth-table';
import { isScenarioLinked } from './total-program-utils';
import { PlainProgramScenario } from '../../models/Scenario';

import {
  Container,
  CustomSelect,
  DepartmentSelect,
  Content,
  ControlsContainer,
  HeadcountSeatcountControlsRow,
  FormTitle,
  SeatCountFactorContainer,
  HeadcountContainer,
  Divider,
} from './styles';

const DROPDOWN_PADDING = 47;

const {
  buildingHeadcountSeatcountDepartments,
  buildingHeadcountSeatcount,
  floorHeadcountSeatcount,
} = GROWTH_CALCULATORS;

export interface IProps {
  scenario: PlainProgramScenario;
  floors?: boolean;
  building: IBuilding;
  isEditingSavedScenario: boolean;
  calculatorType: CalculatorType;
  departments: IDepartment[];
  project: IProject;
  totalPrograms: PlainProgramScenario[];
  showDecimals: boolean;
  calculatorActions?: JSX.Element;
  isRenderingForExport?: boolean;
}

const HeadcountSeatcountScenario: React.FC<IProps> = ({
  scenario,
  totalPrograms,
  floors,
  building,
  isEditingSavedScenario,
  calculatorType,
  departments = [],
  project,
  showDecimals,
  calculatorActions,
  isRenderingForExport,
}) => {
  const dispatch = window.SPWExport ? Function.prototype : useDispatch();
  const showDepartmentSelect =
    calculatorType === BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS ||
    scenario?.calculatorType?.replace(/_/g, '-') ===
      BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS;

  useEffect(() => {
    if (showDepartmentSelect && project?.id) {
      dispatch(getDepartmentsByProjectId(project.id));
    }
  }, [dispatch, showDepartmentSelect, project?.id]);

  const disabledInputs =
    window.SPWExport || isRenderingForExport
      ? false
      : scenario?.id && !isEditingSavedScenario;

  const isDepartment = isDepartmentsTypeScenario(scenario);
  let isLinked = false;
  if (isDepartment) {
    isLinked = isScenarioLinked(scenario?.id, totalPrograms);
  }
  const startYear = scenario?.startYear;

  const handleGrowthYearsSelection = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      if (!scenario?.targetYear) dispatch(setSelectedYear(moment().year()));
      const newGrowthYear = parseInt(event.target.value as string, 10);
      dispatch(setGrowthYears(newGrowthYear));
      const realYear = startYear + newGrowthYear;

      if (realYear < scenario.targetYear) {
        dispatch(setSelectedYear(realYear));
      }
    },
    [dispatch, scenario?.targetYear, startYear]
  );

  const largestDeptNameWidth = useMemo(() => {
    let maxWidth = 0;

    departments.forEach((d) => {
      const departmentNameWidth =
        getWidthForString(`${d.name} ${d.secondaryIdentifier ?? ''}`, {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          fontWeight: '500',
          lineHeight: '24px',
          letterSpacing: 'normal',
        }) + DROPDOWN_PADDING;

      if (departmentNameWidth > maxWidth) {
        maxWidth = departmentNameWidth;
      }
    });

    return maxWidth;
  }, [departments]);

  const largestFloorNameWidth = useMemo(() => {
    let maxWidth = 0;

    building.floors.forEach((f) => {
      const floorNameWidth =
        getWidthForString(f.name, {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          fontWeight: '500',
          lineHeight: '24px',
          letterSpacing: 'normal',
        }) + DROPDOWN_PADDING;

      if (floorNameWidth > maxWidth) {
        maxWidth = floorNameWidth;
      }
    });

    return maxWidth;
  }, [building?.floors]);

  if (!scenario) return null;

  return (
    <Content>
      <Paper elevation={0}>
        {(window.SPWExport || isRenderingForExport) && (
          <ScenarioHeader
            scenario={scenario}
            building={building}
            styles={{ marginBottom: 16 }}
          />
        )}
        <Container>
          <ControlsContainer>
            <HeadcountSeatcountControlsRow>
              {showDepartmentSelect && (
                <FormControl variant="outlined">
                  <DepartmentSelect
                    data-cy="project_departments"
                    label="Departments"
                    defaultValue={
                      scenario?.department?.id || _.head(departments)?.id || ' '
                    }
                    placeholder={_.head(departments)?.name || ' '}
                    value={
                      scenario?.department?.id || _.head(departments)?.id || ' '
                    }
                    style={{
                      maxWidth: '367px',
                      minWidth: '319px',
                      width: largestDeptNameWidth,
                    }}
                    disabled={
                      isRenderingForExport
                        ? false
                        : disabledInputs || isLinked || !departments?.length
                    }
                    onChange={(e) => {
                      const departmentId = e.target.value as number;
                      const department = departments.find(
                        (d: IDepartment) => d.id === departmentId
                      );

                      dispatch(
                        updateScenario(scenario, {
                          department,
                        })
                      );
                    }}
                  >
                    {departments?.length ? (
                      departments.map((department: IDepartment) => (
                        <MenuItem
                          key={`${department.name}-${department.id}`}
                          value={department.id}
                          data-testid={`department-${department.id}`}
                        >
                          <strong style={{ fontWeight: 500 }}>
                            {department.name}
                          </strong>
                          &nbsp;
                          {department.secondaryIdentifier || ''}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value=" " />
                    )}
                  </DepartmentSelect>
                </FormControl>
              )}
              {floors && (
                <FormControl variant="outlined">
                  <CustomSelect
                    label="Floors"
                    value={scenario?.floorId || building?.floors[0]?.id || 0}
                    style={{ minWidth: '136px', width: largestFloorNameWidth }}
                    disabled={
                      window.SPWExport || isRenderingForExport
                        ? false
                        : !isNil(scenario?.id)
                    }
                    onChange={(event) => {
                      const floorId = parseInt(
                        event.target.value as string,
                        10
                      );
                      dispatch(
                        updateScenario(scenario, {
                          floorId,
                        })
                      );
                    }}
                  >
                    {building?.floors?.map((floor: IFloor) => (
                      <MenuItem
                        key={`${floor.name}-${floor.id}`}
                        value={floor.id}
                      >
                        {floor.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              )}
              <FormControl variant="outlined">
                <CustomSelect
                  label="Target Year"
                  forceShrink
                  value={scenario?.targetYear}
                  disabled={
                    isRenderingForExport ? false : disabledInputs || isLinked
                  }
                  onChange={(e) => {
                    const parsedYear = parseInt(e.target.value as string, 10);
                    dispatch(setSelectedYear(parsedYear));
                  }}
                  style={{
                    width: '136px',
                  }}
                >
                  {range(
                    scenario.startYear,
                    scenario.startYear + scenario.growthYears + 1
                  ).map((value, i) => (
                    <MenuItem key={`year-${value}`} value={value}>
                      {i === 0 ? `Current (${value})` : `Year ${i} (${value})`}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </FormControl>
              <FormControl variant="outlined">
                <CustomSelect
                  label="Growth Years"
                  forceShrink
                  disabled={disabledInputs || isLinked}
                  value={scenario?.growthYears || YEARS_AHEAD}
                  onChange={handleGrowthYearsSelection}
                  style={{ width: '159px' }}
                >
                  {reverse(range(1, YEARS_AHEAD + 1)).map(
                    (growthYear: number) => (
                      <MenuItem key={`year-${growthYear}`} value={growthYear}>
                        {`${growthYear} Year${growthYear > 1 ? 's' : ''}`}
                      </MenuItem>
                    )
                  )}
                </CustomSelect>
              </FormControl>
            </HeadcountSeatcountControlsRow>
            {calculatorActions}
          </ControlsContainer>
          <FormTitle style={{ marginBottom: 0 }}>
            CALCULATE WORK SEATS
          </FormTitle>
          <SeatCountFactorContainer container>
            <HeadcountContainer>
              <Headcount
                title="VACANCY FACTOR"
                base={scenario?.headcount}
                tooltip={<VacancyFactorGuide />}
                tooltipHeight={255}
                tooltipWidth={298}
                factorName="vacancyFactor"
                factorValue={scenario?.vacancyFactorPercentage?.toString()}
                resultValue={scenario?.headcountWithVacancy}
                disabled={disabledInputs}
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_left_box"
              />
            </HeadcountContainer>
            <Divider />
            <HeadcountContainer>
              <Headcount
                title="HEADCOUNT UTILIZATION"
                factorName="headcountUtilization"
                base={scenario?.headcountWithVacancy}
                tooltip={<HeadcountUtilizationGuide />}
                tooltipHeight={323}
                tooltipWidth={298}
                factorValue={scenario?.headcountUtilizationPercentage?.toString()}
                resultValue={scenario?.headcountWithUtilization}
                disabled={disabledInputs}
                operator="x"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_middle_box"
              />
            </HeadcountContainer>
            <Divider />
            <HeadcountContainer>
              <Headcount
                title="DESK SHARING RATIO"
                factorName="deskSharingRatio"
                disabled={disabledInputs}
                base={scenario?.headcountWithUtilization}
                rightLabel="Work Seats"
                tooltip={<DeskSharingGuide />}
                tooltipHeight={559}
                tooltipWidth={298}
                showRatio
                factorValue={scenario?.deskSharingRatio?.toString()}
                resultValue={scenario?.workSeats}
                operator="÷"
                showDecimals={showDecimals && !isEditingSavedScenario}
                testingIdPrefix="headcount_calcs_right_box"
              />
            </HeadcountContainer>
          </SeatCountFactorContainer>
        </Container>
        {[
          buildingHeadcountSeatcountDepartments,
          buildingHeadcountSeatcount,
          floorHeadcountSeatcount,
        ].includes(scenario.calculatorType) && (
          <GrowthTable
            isRenderingForExport={isRenderingForExport}
            project={project}
            scenario={scenario}
            calculatorType={calculatorType}
            isEditingSavedScenario={isEditingSavedScenario}
            totalPrograms={totalPrograms}
            testingIdPrefix="growth_calcs"
          />
        )}
      </Paper>
    </Content>
  );
};

HeadcountSeatcountScenario.defaultProps = {
  floors: false,
};

export default HeadcountSeatcountScenario;
